import React, { useEffect, useState } from "react";
import { fetchOutsourcedReport, getCurrency } from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Front Desk — outsourced rooms margin report. Rooms sold through partner
// hotels: revenue we billed the guest vs cost owed to the partner, over a
// date range, with a per-partner breakdown.
// AM/OM are machine drafts (2026-08) — pending native review.
const S = {
  en: {
    title: "Outsourced rooms", subtitle: "Overflow rooms routed to partner hotels — revenue, cost and margin",
    from: "From", to: "To", refresh: "Refresh",
    rooms: "Rooms", nights: "Nights", revenue: "Revenue", cost: "Cost", margin: "Margin",
    partner: "Partner", none: "No outsourced rooms in this range.",
  },
  am: {
    title: "የውጭ ክፍሎች", subtitle: "ወደ አጋር ሆቴሎች የተላኩ ትርፍ ክፍሎች — ገቢ፣ ወጪ እና ትርፍ",
    from: "ከ", to: "እስከ", refresh: "አድስ",
    rooms: "ክፍሎች", nights: "ሌሊቶች", revenue: "ገቢ", cost: "ወጪ", margin: "ትርፍ",
    partner: "አጋር", none: "በዚህ ክልል ውስጥ የውጭ ክፍሎች የሉም።",
  },
  om: {
    title: "Kutaalee ala", subtitle: "Kutaalee dabalataa hoteelota michuutti ergaman — galii, baasii fi bu'aa",
    from: "Irraa", to: "Hamma", refresh: "Haaromsi",
    rooms: "Kutaalee", nights: "Halkanoota", revenue: "Galii", cost: "Baasii", margin: "Bu'aa",
    partner: "Michuu", none: "Hangii kana keessa kutaan ala hin jiru.",
  },
};

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const iso = (offset = 0) => { const d = new Date(); d.setDate(d.getDate() + offset); return d.toISOString().slice(0, 10); };
const TH = { textAlign: "left", color: "#5b6472", padding: "3px 6px" };
const TR = { textAlign: "right" };

export default function OutsourcedReportPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const cur = getCurrency() || "ETB";

  const [from, setFrom] = useState(iso(-29));
  const [to, setTo] = useState(iso(0));
  const [data, setData] = useState(null);

  const load = () => { setData(null); fetchOutsourcedReport({ from, to }).then(setData).catch(() => setData({ totals: {}, by_partner: [] })); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const inp = { padding: 8, borderRadius: 8, border: "1px solid #d8dee2" };
  const totals = (data && data.totals) || {};
  const byPartner = (data && data.by_partner) || [];

  const Metric = ({ label, value, tint, accent }) => (
    <div style={{ background: tint, borderRadius: 14, padding: "16px 18px" }}>
      <div style={{ fontSize: 13, color: "#5f6b78" }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent, marginTop: 4 }}>{value}</div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1040, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 2 }}>{t("title")}</h1>
      <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 14 }}>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("from")} <input type="date" style={{ ...inp, marginLeft: 4 }} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("to")} <input type="date" style={{ ...inp, marginLeft: 4 }} value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <button className="btn primary small" onClick={load}>{t("refresh")}</button>
      </div>

      {data == null ? (
        <p className="muted" style={{ marginTop: 16 }}>…</p>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 14, marginTop: 16 }}>
            <Metric label={t("rooms")} value={totals.rooms || 0} tint="#E6F1FB" accent="#185FA5" />
            <Metric label={t("nights")} value={totals.nights || 0} tint="#E1F5EE" accent="#0F6E56" />
            <Metric label={t("revenue")} value={formatPrice(totals.revenue || 0, cur)} tint="#EAF3DE" accent="#27500A" />
            <Metric label={t("cost")} value={formatPrice(totals.cost || 0, cur)} tint="#FAECE7" accent="#712B13" />
            <Metric label={t("margin")} value={formatPrice(totals.margin || 0, cur)} tint="#FAEEDA" accent="#854F0B" />
          </div>

          {byPartner.length === 0 ? (
            <div style={{ ...CARD, padding: 20, marginTop: 16, textAlign: "center" }} className="muted">{t("none")}</div>
          ) : (
            <div style={{ ...CARD, padding: 14, marginTop: 16 }}>
              <table style={{ width: "100%", fontSize: 13.5, borderCollapse: "collapse" }}>
                <thead><tr>
                  <th style={TH}>{t("partner")}</th>
                  <th style={{ ...TH, ...TR }}>{t("rooms")}</th>
                  <th style={{ ...TH, ...TR }}>{t("nights")}</th>
                  <th style={{ ...TH, ...TR }}>{t("revenue")}</th>
                  <th style={{ ...TH, ...TR }}>{t("cost")}</th>
                  <th style={{ ...TH, ...TR }}>{t("margin")}</th>
                </tr></thead>
                <tbody>
                  {byPartner.map((r) => (
                    <tr key={r.partner_id}>
                      <td style={{ padding: "4px 6px", fontWeight: 600 }}>{r.name}</td>
                      <td style={TR}>{r.rooms || 0}</td>
                      <td style={TR}>{r.nights || 0}</td>
                      <td style={TR}>{formatPrice(r.revenue || 0, cur)}</td>
                      <td style={TR}>{formatPrice(r.cost || 0, cur)}</td>
                      <td style={{ ...TR, color: (r.margin || 0) >= 0 ? "#1D7A44" : "#A32D2D", fontWeight: 600 }}>{formatPrice(r.margin || 0, cur)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: "1px solid #ddd", fontWeight: 700 }}>
                    <td style={{ padding: "5px 6px" }}>{t("margin")}</td>
                    <td style={TR}>{totals.rooms || 0}</td>
                    <td style={TR}>{totals.nights || 0}</td>
                    <td style={TR}>{formatPrice(totals.revenue || 0, cur)}</td>
                    <td style={TR}>{formatPrice(totals.cost || 0, cur)}</td>
                    <td style={TR}>{formatPrice(totals.margin || 0, cur)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
